import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IPaginatedResponse } from '@boma/client-shared';
import {
  IJournalBankEvent,
  IJournalCashInRequest,
  IJournalDepositListRequest,
  IJournalListRequest,
  IJournalListResponse,
  IJournalStatusRequest,
  IJournalStatusResponse,
  JournalType,
} from '@boma/supplier-portal/shared/entities';
import { Observable } from 'rxjs';
import {
  IJournalExpectedClosingCash,
  IJournalStatus,
} from '../../../../entities/src/lib/definitions/i-journal-status';

@Injectable({
  providedIn: 'root',
})
export class JournalService {
  private readonly base: string;
  private readonly version: string;

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private http: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  getStatus(entityId: string, locationId: string): Observable<IJournalStatus> {
    return this.http.get<IJournalStatus>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${locationId}/Status`
    );
  }

  getExpectedClosingCash(entityId: string, locationId: string) {
    return this.http.get<IJournalExpectedClosingCash>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${locationId}/ExpectedClosingCash`
    );
  }

  setOpenCashPosition(
    entityId: string,
    locationId: string,
    requestBody: { amount: number; comment?: string }
  ) {
    return this.http.post<IJournalStatus>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${locationId}/Open`,
      requestBody
    );
  }

  closeCashPosition(
    entityId: string,
    locationId: string,
    requestBody: { amount: number; comment?: string }
  ) {
    return this.http.post<IJournalStatus>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${locationId}/Close`,
      requestBody
    );
  }

  createBankEvent(
    entityId: string,
    locationId: string,
    requestBody: IJournalBankEvent
  ) {
    return this.http.post<IJournalStatus>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${locationId}/Bank`,
      requestBody
    );
  }

  createCashInEvent(
    entityId: string,
    locationId: string,
    requestBody: IJournalCashInRequest
  ) {
    return this.http.post<IJournalStatus>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${locationId}/CashIn`,
      requestBody
    );
  }

  listJournalEvents(
    entityId: string,
    requestBody: IJournalListRequest = {},
    page = 1,
    perPage = 25
  ) {
    const queryParams = [`page=${page}`, `pageSize=${perPage}`];
    const urlQuery = `?${queryParams.join('&')}`;
    return this.http.post<IPaginatedResponse<IJournalListResponse>>(
      `${this.base}api/v${this.version}/${entityId}/Journal/List${urlQuery}`,
      requestBody
    );
  }

  updateJournalEvent(
    entityId: string,
    id: string,
    requestBody: {
      reference: string;
      amount: number;
      locationId: string;
      bankEventType: string;
      comment: string;
      type: any;
      nameOfLocation: string;
    },
    type: JournalType // JournalType
  ) {
    return this.http.put<IJournalListResponse>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${type}/${id}`,
      requestBody
    );
  }

  updateBankEvent(
    entityId: string,
    id: string,
    requestBody: IJournalBankEvent
  ) {
    return this.http.put<IJournalListResponse>(
      `${this.base}api/v${this.version}/${entityId}/Journal/Bank/${id}`,
      requestBody
    );
  }

  updateOpenEvent(
    entityId: string,
    id: string,
    requestBody: { locationId: string; amount: number; comment?: string }
  ) {
    return this.http.put<IJournalListResponse>(
      `${this.base}api/v${this.version}/${entityId}/Journal/Open/${id}`,
      requestBody
    );
  }

  updateCloseEvent(
    entityId: string,
    id: string,
    requestBody: { locationId: string; amount: number; comment?: string }
  ) {
    return this.http.put<IJournalListResponse>(
      `${this.base}api/v${this.version}/${entityId}/Journal/Close/${id}`,
      requestBody
    );
  }

  deleteJournalEntry(entityId: string, id: string) {
    return this.http.delete<any>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${id}`
    );
  }

  restoreJournalEntry(entityId: string, id: string) {
    return this.http.patch<IJournalListResponse>(
      `${this.base}api/v${this.version}/${entityId}/Journal/${id}`,
      {}
    );
  }

  //Returns us the user, location and status of their till with cash position
  listJournalUserStatus(
    entityId: string,
    requestBody: IJournalStatusRequest | {},
    page = 1,
    perPage = 10,
    startDate = ''
  ) {
    const queryParams = [
      `page=${page}`,
      `pageSize=${perPage}`,
      `startDate=${startDate}`,
    ];
    const urlQuery = `?${queryParams.join('&')}`;
    return this.http.post<IPaginatedResponse<IJournalStatusResponse>>(
      `${this.base}api/v${this.version}/${entityId}/Journal/UserStatus/List${urlQuery}`,
      requestBody
    );
  }

  verifyBankEvent(
    entityId: string,
    requestBody: string[] // array of deposit ids to verify
  ) {
    return this.http.post<any>(
      `${this.base}api/v${this.version}/${entityId}/Journal/Bank/Verify`,
      requestBody
    );
  }

  listJournalDeposits(
    entityId: string,
    requestBody: IJournalDepositListRequest = {},
    page = 1,
    perPage = 25
  ) {
    const queryParams = [`page=${page}`, `pageSize=${perPage}`];
    const urlQuery = `?${queryParams.join('&')}`;
    return this.http.post<IPaginatedResponse<IJournalListResponse>>(
      `${this.base}api/v${this.version}/${entityId}/Journal/Bank/List${urlQuery}`,
      requestBody
    );
  }
}
